import React, { useState, useEffect } from 'react';
import { Col, Row, Tabs } from 'antd';
import { useLazyQuery, useReactiveVar } from '@apollo/client';
import operations from 'apollo/operations';
import { GuestUser, User } from 'apollo/types';
import { AccountsTable, InvitationTable, UsersTable } from 'components/user';
import { concessionaireName } from 'apollo/cache';
import { Button, Input, PageHeader } from '@cloudcar-app/cloudcar-components';
import { useHistory } from 'react-router-dom';
import { SearchOutlined } from '@ant-design/icons';
import styles from './Users.module.scss';

type UsersTabs = 'createdUsers' | 'invitation';
const { TabPane } = Tabs;

const UserList: React.FC = () => {
  const history = useHistory();
  const [userList, setUserList] = useState<User[]>([]);
  const [guestUserList, setGuestUserList] = useState<GuestUser[]>([]);
  const [activeTab, setActiveTab] = useState<UsersTabs>('createdUsers');
  const concessionaire = useReactiveVar(concessionaireName);
  const [searchString, setSearchString] = useState<string>('');

  const [getAllUsers, { data, loading, refetch }] = useLazyQuery<{
    listUser: User[];
  }>(operations.GET_ALL_USERS, { notifyOnNetworkStatusChange: true });

  const [
    getAllGuestUsers,
    {
      data: guestUsersData,
      loading: guestUsersLoading,
      refetch: refetchGuestUsers,
    },
  ] = useLazyQuery<{ listGuestUser: GuestUser[] }>(
    operations.GET_ALL_GUEST_USERS,
  );

  const queryUsers = () => {
    if (concessionaire) {
      getAllUsers();
      getAllGuestUsers({
        variables: {
          concessionaireName: concessionaire,
        },
      });
    }
  };

  const settingGuestUserList = () => {
    if (guestUsersData && guestUsersData.listGuestUser) {
      setGuestUserList(guestUsersData.listGuestUser);
    }
  };

  useEffect(() => {
    queryUsers();
  }, [concessionaire]);

  useEffect(() => {
    if (data && data.listUser) {
      setUserList(data.listUser);
    }
  }, [data]);

  useEffect(() => {
    if (searchString && data?.listUser) {
      const filteredUserList = data?.listUser.filter((user) => {
        const { name, username, email, firstName } = user;
        return (
          name?.toLowerCase().includes(searchString.toLowerCase()) ||
          username?.toLowerCase().includes(searchString.toLowerCase()) ||
          email?.toLowerCase().includes(searchString.toLowerCase()) ||
          firstName?.toLowerCase().includes(searchString.toLowerCase())
        );
      });

      setUserList(filteredUserList);
    }
  }, [searchString, data]);

  useEffect(() => {
    settingGuestUserList();
  }, [guestUsersData]);

  return (
    <>
      <Row justify="space-between" align="middle">
        <PageHeader title="Usuarios" />
        <Row justify="space-between" gutter={12}>
          <Col>
            <Button onClick={() => history.push('/createUser')}>
              Crear usuario
            </Button>
          </Col>
        </Row>
      </Row>
      <Row>
        <Col xs={24}>
          <Input
            placeholder="Buscar..."
            value={searchString}
            onChange={(event) => setSearchString(event.target.value)}
            className="CustomSearchInput"
            suffix={<SearchOutlined />}
            compact
          />
        </Col>
      </Row>

      <Tabs
        className={styles.Tabs}
        activeKey={activeTab}
        onChange={(key) => setActiveTab(key as UsersTabs)}
        size="large"
      >
        <TabPane tab="Usuarios creados" key="createdUsers">
          <UsersTable
            loading={loading}
            data={userList}
            refetchUsers={refetch}
          />
        </TabPane>
        <TabPane tab="Administración de usuarios" key="accounts">
          <AccountsTable
            loading={loading}
            data={userList}
            refetchUsers={refetch}
          />
        </TabPane>
        <TabPane tab="Invitaciones" key="invitation">
          <InvitationTable
            loading={guestUsersLoading}
            data={guestUserList}
            refetchUsers={refetchGuestUsers}
          />
        </TabPane>
      </Tabs>
    </>
  );
};

export default UserList;
